import {AfterViewInit, Component, ElementRef, OnInit, Renderer2, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {MatPaginator} from "@angular/material/paginator";
import {MatSort} from "@angular/material/sort";
import {UtilsService} from "../shared/utils.service";
import {AppService} from "../app.service";
import {ApiRequestService} from "../shared/api-request.service";
import {MatDialog} from "@angular/material/dialog";
import {tap} from "rxjs/operators";
import {UserPublicProfileComponent} from "../shared/user-public-profile/user-public-profile.component";
import {ApiDataSource} from "../utils/api-data-source";
import {VendorModel} from "../models/vendor.model";
import {VendorsEditComponent} from "./vendors-edit/vendors-edit.component";
import {VendorsViewComponent} from "./vendors-view/vendors-view.component";
import {VendorsFilterComponent} from "./vendors-filter/vendors-filter.component";
import {saveAs} from "file-saver";
import moment from "moment";
import {AssetsService} from "../assets/assets.service";
import {RBACService} from "../shared/rbac.service";
import {Router} from "@angular/router";

@Component({
  selector: 'app-vendors',
  templateUrl: './vendors.component.html',
  styleUrls: ['./vendors.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class VendorsComponent implements OnInit, AfterViewInit {

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  dataSource = new VendorsDataSource(this.app, this.api);

  // RBAC permission flags: resolved once in ngOnInit().
  canCreate: boolean = false;
  canView: boolean = false;
  canEdit: boolean = false;
  canDelete: boolean = false;

  // Build displayedColumns dynamically (NOT as a static array).
  displayedColumns: string[] = [];

  on_parent_list: boolean = true;

  @ViewChild('fileInput') fileInput: ElementRef;

  constructor(
    private utils: UtilsService,
    public assetsService: AssetsService,
    public app: AppService,
    private api: ApiRequestService,
    private dialog: MatDialog,
    public renderer: Renderer2,
    public rbac: RBACService,
    private router: Router
  ) { }

  ngOnInit() {
    // 1. Resolve RBAC permission flags.
    this.canCreate = this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_REGISTERS_VENDORS, RBACService.RBAC_A.A_CREATE)
    ], true);
    this.canView = this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_REGISTERS_VENDORS, RBACService.RBAC_A.A_VIEW)
    ], true);
    this.canEdit = this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_REGISTERS_VENDORS, RBACService.RBAC_A.A_EDIT)
    ], true);
    this.canDelete = this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_REGISTERS_VENDORS, RBACService.RBAC_A.A_DELETE)
    ], true);

    // 2. Page-level access: redirect if user has NO permissions at all.
    if (!this.canCreate && !this.canView && !this.canEdit && !this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to access vendors.');
      this.router.navigate(['/dashboard']);
      return;
    }

    // 3. Build displayedColumns dynamically.
    this.displayedColumns = [];
    if (this.canView || this.canDelete) {
      this.displayedColumns.push('select');
    }
    this.displayedColumns.push(
      'id',
      'name',
      'is_prequalified',
      'insurance_last_verified',
      'insurance_valid_till',
      'contact_details',
      'linked_assets_count',
      'created_by_user',
      'date_created',
      // 'date_created_UTC',
      'buttons'
    );

    // 4. Only load data if user has view permission.
    if (this.canView) {
      this.dataSource.getData(false);
    }
  }

  ngAfterViewInit() {
    // The table, its matSort and its mat-paginator all live inside a container gated by
    // *ngIf="canView", so without that permission Angular never creates those view
    // children and this.sort / this.paginator stay undefined. Bail out before wiring them up.
    if (!this.canView) {
      return;
    }

    // Reset the paginator when sorting
    this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));

    const _tap = tap(() => {
      this.dataSource.limit = this.paginator.pageSize;
      this.dataSource.offset = this.paginator.pageIndex;
      this.dataSource.order = this.sort.direction;

      // sorting for utc time by parsing original time
      if (this.sort.active === "date_created_UTC") {
        this.dataSource.order_by = "date_created";
      } else {
        this.dataSource.order_by = this.sort.active;
      }


      this.dataSource.getData(false);
    });

    // Subscribe to the paginator tap events
    this.paginator.page.pipe(_tap).subscribe();
    this.sort.sortChange.pipe(_tap).subscribe();
  }

  /**
   * Create a new Vendor.
   *
   * @return {void}
   */
  onAdd(): void {
    if (!this.canCreate) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to create vendors.');
      return;
    }
    // Deliberately not routed through onEdit(), which would check the edit permission and block a
    // create-only user.
    this.openDialog(new VendorModel());
  }

  /**
   * Update an existing Vendor.
   *
   * @param {VendorModel} vendor The vendor to be edited.
   * @return {void}
   */
  onEdit(vendor: VendorModel): void {
    if (!this.canEdit) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to edit vendors.');
      return;
    }
    this.openDialog(vendor);
  }

  /**
   * Opens the create/edit dialog. Shared by onAdd() and onEdit(), which each guard their own
   * permission before calling this.
   *
   * @param {VendorModel} vendor The vendor to be created or edited.
   * @return {void}
   */
  private openDialog(vendor: VendorModel): void {
    this.dialog
      .open(VendorsEditComponent, {
        width: '1200px',
        data: {
          vendor_id: vendor.id
        }
      })
      .afterClosed()
      .subscribe(() => {
        // Refresh the list regardless of how the dialog is closed, but only for a user who may
        // actually see the list.
        if (this.canView) {
          this.dataSource.getData(true);
        }
      });
  }

  /**
   * Displays Vendor data.
   *
   * @param {number} id - The unique identifier of the vendor.
   * @return {void}
   */
  onView(id: number): void {
    if (!this.canView) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to view vendors.');
      return;
    }
    this.utils.showComponentDialog(
      VendorsViewComponent,
      id,
      { width: '700px' },
      () => {
        // Refresh the list regardless of how the dialog is closed.
        this.dataSource.getData(true);
      }
    );
  }

  /**
   * Archives selected Vendors.
   *
   * @return {void}
   */
  onRemoveSelected(): void {
    if (!this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to archive vendors.');
      return;
    }
    this.utils.showModal(
      'Archive Vendor(s)',
      `Are you sure you want to archive ${this.dataSource.selection.selected.length} vendor(s)?`,
      () => {
        this.remove(this.dataSource.selection.selected.join(','), 'Vendor(s) have been archived.');
      }
    );
  }

  /**
   * Archives the specified Vendor.
   *
   * @param {VendorModel} vendor - The vendor to be archived.
   * @return {void}
   */
  onRemove(vendor: VendorModel): void {
    if (!this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to archive vendors.');
      return;
    }
    this.utils.showModal(
      'Archive Vendor',
      `Are you sure you want to archive "${vendor.name}"?`,
      () => {
        this.remove(vendor.id, 'Vendor has been archived.');
      }
    );
  }

  /**
   * Archives Vendors with the specified IDs.
   *
   * @param {any} ids
   * @param msg
   * @return {void}
   */
  private remove(ids: any, msg: string): void {
    this.api.makeRequest('delete', `v2/vendors/${ids}`)
      .then((response) => {
        this.utils.showToast(msg);
        this.dataSource.selection.clear();
        this.dataSource.getData(false);
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      });
  }

  /**
   * Restore Vendor.
   *
   * @return {void}
   */
  onRestore(id: number): void {
    // Restoring is the inverse of archiving, so it is governed by the delete permission.
    if (!this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to restore vendors.');
      return;
    }
    this.utils.showModal(
      'Restore Vendor',
      'Are you sure you want to restore this vendor?',
      () => {
        this.restore(id, 'The Vendor was restored.');
      }
    );
  }

  /**
   * Restore selected Vendors.
   *
   * @return {void}
   */
  onRestoreSelected(): void {
    // Restoring is the inverse of archiving, so it is governed by the delete permission.
    if (!this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to restore vendors.');
      return;
    }
    this.utils.showModal(
      'Restore Selected Vendors',
      'Are you sure you want to restore the selected Vendors?',
      () => {
        this.restore(this.dataSource.selection.selected.join(','), 'The selected Vendors were restored.');
      }
    );
  }

  /**
   * Restores Vendors with the specified IDs.
   *
   * @param {any} ids
   * @param msg
   * @return {void}
   */
  private restore(ids: any, msg: string): void {
    this.api.makeRequest('put', `v2/vendors/${ids}/restore`)
      .then((response) => {
        this.utils.showToast(msg);
        this.dataSource.selection.clear();
        this.dataSource.getData();
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      });
  }

  /**
   * Show component dialog for filtering Vendors.
   *
   * @return {void}
   */
  onFilter(): void {
    // Filtering only narrows the list, so it is meaningless without the view permission.
    if (!this.canView) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to view vendors.');
      return;
    }
    this.utils.showComponentDialog(VendorsFilterComponent, {
        filter_is_prequalified: this.dataSource.is_prequalified,
        filter_archived: this.dataSource.archived,
        date_range: this.dataSource.insurance_expiry_date
      }, {
        width: '768px'
      },
      (results: any): void => {
        // Terminate early if it is undefined or an empty string.
        if ( typeof results === 'undefined' || results === '' ) {
          return;
        }

        // Apply the filters.
        this.dataSource.is_prequalified = results.is_prequalified ?? this.dataSource.is_prequalified;
        this.dataSource.archived = results.archived ?? this.dataSource.archived;
        this.dataSource.insurance_expiry_date = results.date_range ?? this.dataSource.insurance_expiry_date;
        this.dataSource.getData();
      }
    );
  }

  /**
   * Exports the list of Vendors into the specified format and sends a download request to the browser.
   *
   * @param type csv, xls, xlsx, pdf.
   */
  onExportSelected(type: string = 'csv'): void {
    // Exporting reveals the records themselves, so it requires the view permission.
    if (!this.canView) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to export vendors.');
      return;
    }
    this.dataSource.makeDownloadRequest(`v2/vendors/export/${type}` + (this.dataSource.selection.selected.length ? '/' + this.dataSource.selection.selected.join(',') : ''), {
      is_prequalified: this.dataSource.is_prequalified,
      archived: this.dataSource.archived,
      insurance_expiry_date_range: (this.dataSource.insurance_expiry_date && this.dataSource.insurance_expiry_date.length > 1) ? this.dataSource.insurance_expiry_date.map((date: Date) => {
        return date.getTime() / 1000;
      }).join(',') : ''
    })
      .then((response) => {
        saveAs(response, `Vendors - ${moment().toString()}.${type}`);
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      });
  }

  /**
   * Open the User's public profile dialog.
   *
   * @param hash
   * @return {void}
   */
  onUserPublicView(hash: string): void {
    this.utils.showComponentDialog(
      UserPublicProfileComponent,
      hash,
      { width: '90%' },
      () => {
        // Refresh the list regardless of how the dialog is closed.
        // this.dataSource.getData();
      }
    );
  }

  protected readonly RBACService = RBACService;
  protected readonly AppService = AppService;
}

export class VendorsDataSource extends ApiDataSource {
  order_by = 'id';
  order = 'asc';
  is_prequalified = "";
  archived = "false";
  insurance_expiry_date: Date[] = [];

  getData(resetOffset: boolean = false) {
    this.makeRequest('v2/vendors', resetOffset, {
      is_prequalified: this.is_prequalified,
      archived: this.archived,
      insurance_expiry_date_range: this.insurance_expiry_date.map((date: Date) => {
        return date.getTime() / 1000;
      }).join(',')
    });
  }
}
